/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IDobbiesCheckoutCncDetailsViewProps } from './dobbies-checkout-cnc-details';
import Button from 'reactstrap/lib/Button';
import { getCheckoutState } from '@msdyn365-commerce/global-state';

export default (props: IDobbiesCheckoutCncDetailsViewProps) => {
    const [emailValue, setEmailValue] = React.useState('');
    const [numOfCnCItems, setNumOfCnCItems] = React.useState(0);
    const [isEmailValid, setIsEmailValid] = React.useState(false);
    const [isCNCSecondScreen, setIsCNCSecondScreen] = React.useState(false);
    const [emailError, setEmailError] = React.useState<string | null>(null);
    const [emailTouched, setEmailTouched] = React.useState(false);

    const isValidEmail = (value?: string) => {
        if (!value) {
            return 'Email is missing.';
        }

        const parts = value.split('@');

        if (parts.length !== 2) {
            if (parts.length === 1) {
                return 'Email is missing the @ character.';
            }
            if (parts.length > 2) {
                return 'Email has more than one @ character.';
            }
        }

        if (!parts[0]) {
            return 'Email must have characters before the @.';
        }

        if (!/\.[^@]+$/.test(parts[1] || '')) {
            return 'Email is missing the domain i.e. .co.uk.';
        }
        if (!/@[^@.]+\./.test(value)) {
            return 'Email must have a domain name between @ and .';
        }
        if (/\s/.test(value)) {
            return 'Email must not contain spaces.';
        }
        return null;
    };

    React.useEffect(() => {
        getCheckoutState(props.context.actionContext).then(res => {
            // let numberOfCncProducts = 0;
            // res.checkoutCart.cart.CartLines?.map(cl => {
            //     if (cl.FulfillmentStoreId) {
            //         numberOfCncProducts += 1;
            //     }
            // });
            const cncCartLines = res.checkoutCart?.cart?.CartLines?.filter(item => item.FulfillmentStoreId) || [];
            const totalQtyOfCNC =
                cncCartLines?.reduce((total: any, item: any) => {
                    return total + (item.Quantity || 0);
                }, 0) ?? 0;
            setNumOfCnCItems(totalQtyOfCNC);
        });
    }, []);

    const checkoutState = props.data.checkout.result;

    React.useEffect(() => {
        if (props.email) {
            setEmailValue(props.email);
            return;
        }

        if (checkoutState && checkoutState?.shippingAddress && checkoutState.shippingAddress.Email) {
            const receiptEmail = checkoutState.shippingAddress.Email;
            setEmailValue(receiptEmail || '');
        }
    }, [checkoutState?.shippingAddress, props.email]);

    React.useEffect(() => {
        const error = isValidEmail(emailValue);
        setIsEmailValid(error === null);
        setEmailError(error);
    }, [emailValue]);

    return (
        <div className={`row cnc-accordion-content ${!isCNCSecondScreen ? 'less-padding' : 'more-padding'}`}>
            {props.isBeingEdited ? (
                <>
                    <div className='ms-checkout__line-items-group-count-cost w-100'>
                        <span>{numOfCnCItems !== 1 ? `${numOfCnCItems} items` : `${numOfCnCItems} item`}</span>
                        <span>Collection cost: Free</span>
                    </div>
                    <div className='ms-checkout__line-items-group-text w-100'>
                        <p>Please confirm your collection details.</p>
                    </div>

                    <div
                        className={`msc-address-form__item msc-address-form__item-email w-100 ${
                            !isEmailValid && emailTouched ? 'address-form__item-invalid' : ''
                        }`}
                        id='email_addressemail_container'
                    >
                        <label className='msc-address-form__label' htmlFor='email_addressemail'>
                            Email*:
                        </label>
                        {!isEmailValid && emailTouched && (
                            <span className='msc-address-form__alert' role='alert' aria-live='assertive'>
                                {emailError}
                            </span>
                        )}
                        <input
                            id='email_addressemail'
                            name='Email'
                            className='msc-address-form__input msc-address-form__input-text'
                            type='email'
                            onChange={e => {
                                if (!emailTouched) {
                                    setEmailTouched(true);
                                }
                                setEmailValue(e.target.value);
                            }}
                            value={emailValue}
                            aria-describedby={!isEmailValid && emailTouched ? 'email_addressemail_container' : undefined}
                            aria-invalid={!isEmailValid && emailTouched}
                        />
                    </div>
                </>
            ) : (
                <>
                    {isCNCSecondScreen && (
                        <>
                            <button
                                className='cnc-edit'
                                onClick={() => {
                                    props.onEdit();
                                    setIsCNCSecondScreen(false);
                                }}
                            >
                                Edit
                            </button>
                        </>
                    )}
                    <div className='ms-checkout__line-items-group-count-cost w-100'>
                        <span>{numOfCnCItems !== 1 ? `${numOfCnCItems} items` : `${numOfCnCItems} item`}</span>
                        <span>Collection cost: Free</span>
                    </div>
                    <div className={`ms-checkout__line-items-group-text w-100 ${isCNCSecondScreen ? 'd-none' : ''}`}>
                        <p>Please confirm your collection details.</p>
                    </div>
                    <div className={`ms-checkout__cnc-info ${!isCNCSecondScreen ? 'with-bg' : 'no-bg'}`}>
                        <p>{props.fullName}</p>
                        <p>{props.email}</p>
                        <p>{props.phone}</p>

                        {!isCNCSecondScreen && (
                            <button
                                className=''
                                onClick={() => {
                                    props.onEdit();
                                    setIsCNCSecondScreen(false);
                                }}
                            >
                                Edit
                            </button>
                        )}
                    </div>
                </>
            )}
            {props.isBeingEdited ? (
                <Button
                    className={!isEmailValid ? 'msc-btn-default w-100 msc-address-form__button-updating' : 'msc-btn-default w-100'}
                    disabled={!isEmailValid}
                    onClick={() => {
                        if (props.onSaveDetails) {
                            props.onSaveDetails(emailValue);
                        }
                        setIsCNCSecondScreen(false);
                    }}
                >
                    Save Click & Collect details
                </Button>
            ) : (
                <>
                    {!isCNCSecondScreen && (
                        <>
                            <Button
                                className='msc-btn-default w-100'
                                onClick={() => {
                                    if (props.onContinueToPayment) {
                                        props.onContinueToPayment();
                                    }
                                    setIsCNCSecondScreen(true);
                                }}
                            >
                                Continue to payment
                            </Button>
                        </>
                    )}
                </>
            )}
        </div>
    );
};
